import type { OnChanges, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  HostBinding,
  Input,
} from '@angular/core';
import { AnimationBehavior } from '@freelancer/directives';
import { ContainerSize } from '@freelancer/ui/container';
import { HorizontalAlignment, VerticalAlignment } from '@freelancer/ui/grid';
import { PictureDisplay } from '@freelancer/ui/picture';
import { SectionFeatureBodyComponent } from './section-feature-body.component';
import { SectionFeatureButtonDetailComponent } from './section-feature-button-detail.component';
import { SectionFeatureButtonComponent } from './section-feature-button.component';
import { SectionFeatureDetailComponent } from './section-feature-detail.component';
import { SectionFeatureHeadingComponent } from './section-feature-heading.component';
import { SectionFeatureSubheadingComponent } from './section-feature-subheading.component';
import {
  SectionFeatureAlignment,
  SectionFeatureBgColor,
  SectionFeatureColor,
  SectionFeatureSize,
} from './section-feature.types';

@Component({
  standalone: false,
  selector: 'fl-section-feature',
  template: `
    <div
      class="SectionFeature"
      [attr.data-size]="size"
      [attr.data-border-top]="borderTop"
      [attr.data-image-overflow]="imageOverflow"
      [attr.data-overlay]="overlay"
      [attr.data-full-width]="fullWidth"
      [attr.data-color]="color"
      [attr.data-bg-color]="backgroundColor"
    >
      <fl-container
        [size]="imageOverflow ? ContainerSize.FLUID : containerSize"
      >
        <fl-grid
          [vAlign]="VerticalAlignment.VERTICAL_CENTER"
          [hAlign]="HorizontalAlignment.HORIZONTAL_CENTER"
        >
          <fl-col
            class="SectionFeature-content {{
              alignment === SectionFeatureAlignment.LEFT
                ? 'mb-large'
                : 'mb-none'
            }} {{
              fullWidth && alignment === SectionFeatureAlignment.LEFT
                ? 'desktop:mb-large'
                : 'desktop:mb-none'
            }}"
            [attr.data-image-overflow]="imageOverflow"
            [col]="12"
            [colDesktopSmall]="
              hideImageDesktopSmall
                ? 12
                : fullWidth
                  ? 8
                  : !isImageMainContent
                    ? 7
                    : 5
            "
            [colDesktopLarge]="fullWidth ? 8 : !isImageMainContent ? 7 : 5"
            [colOrder]="contentColOrder"
            [colTabletOrder]="contentColOrderTablet"
            [colDesktopSmallOrder]="contentColOrderDesktopSmall"
            [pull]="fullWidth ? undefined : 'right'"
            [flAnimateOnScroll]="animateOnScroll"
            [flAnimateOnScrollBehavior]="
              alignment === SectionFeatureAlignment.RIGHT && animateOnScroll
                ? AnimationBehavior.FADE_FROM_RIGHT
                : AnimationBehavior.FADE_FROM_LEFT
            "
          >
            @if (sectionFeatureDetail) {
              <div
                class="mb-small text-body-16 font-normal {{
                  color === SectionFeatureColor.DARK
                    ? 'text-foreground'
                    : 'text-light'
                }}"
              >
                <ng-content select="fl-section-feature-detail"></ng-content>
              </div>
            }
            @if (sectionFeatureHeading) {
              <h2
                class="text-body-24 tablet:text-title-32 desktop:text-title-40 mb-mid"
                [class.text-foreground]="color === SectionFeatureColor.DARK"
                [class.text-light]="!(color === SectionFeatureColor.DARK)"
              >
                <ng-content select="fl-section-feature-heading"></ng-content>
              </h2>
            }
            @if (sectionFeatureSubheading) {
              <div
                class="mb-small text-body-16 desktop:text-body-20 font-normal {{
                  color === SectionFeatureColor.DARK
                    ? 'text-foreground'
                    : 'text-light'
                }}"
                [flAnimateOnScroll]="animateOnScroll"
              >
                <ng-content select="fl-section-feature-subheading"></ng-content>
              </div>
            }
            <div>
              @if (sectionFeatureBody) {
                <div
                  class="text-body-16 desktop:text-body-16 font-normal {{
                    color === SectionFeatureColor.DARK
                      ? 'text-foreground'
                      : 'text-light'
                  }} {{ sectionFeatureButton ? 'mb-xlarge' : 'mb-none' }}"
                >
                  <ng-content select="fl-section-feature-body"></ng-content>
                </div>
              }
              @if (sectionFeatureButton) {
                <div
                  class="{{
                    sectionFeatureButtonDetail ? 'mb-xxsmall' : 'mb-none'
                  }}"
                >
                  <ng-content select="fl-section-feature-button"></ng-content>
                </div>
              }
              <p class="text-body-16">
                <ng-content
                  select="fl-section-feature-button-detail"
                ></ng-content>
              </p>
            </div>
          </fl-col>
          <fl-col
            class="SectionFeature-image {{
              alignment === SectionFeatureAlignment.RIGHT && !imageOverflow
                ? 'mb-large'
                : 'mb-none'
            }} {{
              fullWidth && alignment === SectionFeatureAlignment.RIGHT
                ? 'desktop:mb-large'
                : 'desktop:mb-none'
            }}"
            [col]="12"
            [attr.data-image-overflow]="imageOverflow"
            [attr.data-hide-desktop-small]="hideImageDesktopSmall"
            [colDesktopSmall]="fullWidth ? 12 : isImageMainContent ? 7 : 5"
            [colOrder]="imageColOrder"
            [colTabletOrder]="imageColOrderTablet"
            [colDesktopSmallOrder]="imageColOrderDesktopSmall"
            [pull]="fullWidth ? undefined : 'right'"
            [flAnimateOnScroll]="animateOnScroll"
            [flAnimateOnScrollBehavior]="
              alignment === SectionFeatureAlignment.RIGHT && animateOnScroll
                ? AnimationBehavior.FADE_FROM_LEFT
                : AnimationBehavior.FADE_FROM_RIGHT
            "
            [class.mobile:hide]="hideImageMobile"
            [class.tablet:hide]="hideImageTablet"
          >
            <figure class="SectionFeature-image-figure">
              <fl-picture
                class="SectionFeature-image-figure-picture"
                [attr.data-image-overflow]="imageOverflow"
                [alignCenter]="true"
                [boundedWidth]="!imageOverflow"
                [fixedAspectRatio]="imageOverflow"
                [display]="PictureDisplay.BLOCK"
                [src]="imageSrc"
                [alt]="imageAlt"
              ></fl-picture>
              @if (imageCaption) {
                <figcaption class="SectionFeature-figure-caption">
                  <span
                    class="text-body-16 {{
                      color === SectionFeatureColor.DARK
                        ? 'text-foreground'
                        : 'text-light'
                    }}"
                  >
                    {{ imageCaption }}
                  </span>
                </figcaption>
              }
            </figure>
          </fl-col>
        </fl-grid>
      </fl-container>
    </div>
  `,
  styleUrls: ['./section-feature.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionFeatureComponent implements OnChanges {
  AnimationBehavior = AnimationBehavior;

  @Input() alignment: SectionFeatureAlignment;
  @Input() alignmentTablet?: SectionFeatureAlignment;
  @Input() alignmentDesktopSmall?: SectionFeatureAlignment;
  @Input() borderTop = false;

  @HostBinding('attr.data-bg-color')
  @Input()
  backgroundColor?: SectionFeatureBgColor;

  @HostBinding('attr.data-color')
  @Input()
  color: SectionFeatureColor = SectionFeatureColor.DARK;

  @Input() containerSize: ContainerSize = ContainerSize.DESKTOP_LARGE;
  @Input() imageSrc: string;
  @Input() imageAlt: string;
  @Input() imageCaption?: string;
  /** Stack the content and featured image on top of each other. */
  @Input() fullWidth = false;
  @Input() overlay = false;
  @Input() size: SectionFeatureSize;
  /** If true, image will take up more space on the grid, else the body will */
  @Input() isImageMainContent = true;
  @Input() hideImageMobile = false;
  @Input() hideImageTablet = false;
  @Input() hideImageDesktopSmall = false;
  /** If true, image is full height and full width, without paddings */
  @Input() imageOverflow = false;
  @Input() animateOnScroll = false;

  @ContentChild(SectionFeatureDetailComponent)
  sectionFeatureDetail: SectionFeatureDetailComponent;
  @ContentChild(SectionFeatureHeadingComponent)
  sectionFeatureHeading: SectionFeatureHeadingComponent;
  @ContentChild(SectionFeatureSubheadingComponent)
  sectionFeatureSubheading: SectionFeatureSubheadingComponent;
  @ContentChild(SectionFeatureBodyComponent)
  sectionFeatureBody: SectionFeatureBodyComponent;
  @ContentChild(SectionFeatureButtonComponent)
  sectionFeatureButton: SectionFeatureButtonComponent;
  @ContentChild(SectionFeatureButtonDetailComponent)
  sectionFeatureButtonDetail: SectionFeatureButtonDetailComponent;

  PictureDisplay = PictureDisplay;
  HorizontalAlignment = HorizontalAlignment;
  VerticalAlignment = VerticalAlignment;
  SectionFeatureAlignment = SectionFeatureAlignment;
  SectionFeatureColor = SectionFeatureColor;
  ContainerSize = ContainerSize;

  // NOTE: Type is explicitly set to 1 | 2 instead of `number` to avoid type error
  contentColOrder: 1 | 2;
  contentColOrderTablet?: 1 | 2;
  contentColOrderDesktopSmall?: 1 | 2;
  imageColOrder: 1 | 2;
  imageColOrderTablet?: 1 | 2;
  imageColOrderDesktopSmall?: 1 | 2;

  ngOnChanges(changes: SimpleChanges): void {
    if (this.alignment && 'alignment' in changes) {
      this.imageColOrder = this.getColOrder(this.alignment, false);
      this.contentColOrder = this.getColOrder(this.alignment, true);
    }

    if (this.alignmentTablet && 'alignmentTablet' in changes) {
      this.imageColOrderTablet = this.getColOrder(this.alignmentTablet, false);
      this.contentColOrderTablet = this.getColOrder(this.alignmentTablet, true);
    }

    if (this.alignmentDesktopSmall && 'alignmentDesktopSmall' in changes) {
      this.imageColOrderDesktopSmall = this.getColOrder(
        this.alignmentDesktopSmall,
        false,
      );
      this.contentColOrderDesktopSmall = this.getColOrder(
        this.alignmentDesktopSmall,
        true,
      );
    }
  }

  // If alignment is 'left', the content should be col order 1, and image is col order 2
  // If alignment is 'right', the content should be col order 2, and the image is col order 1
  getColOrder(alignment: SectionFeatureAlignment, isContent: boolean): 1 | 2 {
    if (isContent) {
      return alignment === SectionFeatureAlignment.LEFT ? 1 : 2;
    }
    return alignment === SectionFeatureAlignment.LEFT ? 2 : 1;
  }
}
